
import { useState } from "react";
import { useClientOperations } from "@/contexts/client-operations";
import { useToast } from "@/hooks/use-toast";
import type { Client } from "@/types";

export interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  age: number;
  gender: string;
  goals: string;
  notes: string;
}

export function useClientForm(client: Client) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    name: client.name || "",
    email: client.email || "",
    phone: client.phone || "",
    age: client.age || 0,
    gender: client.gender || "other",
    goals: client.goals || "",
    notes: typeof client.notes === 'string' ? client.notes : ""
  });
  const { updateClientDetails } = useClientOperations();
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Convert to expected format for API
      const clientData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: Number(formData.age),
        gender: formData.gender as "male" | "female" | "other",
        goals: formData.goals,
        notes: formData.notes // Make sure this is just a string
      };

      await updateClientDetails(client.id, clientData);
      
      toast({
        title: "Success",
        description: "Client information updated successfully",
      });
      
      setIsEditing(false);
      
      // Force page refresh to show updated data
      window.location.reload();
    } catch (error) {
      console.error("Error updating client:", error);
      toast({
        title: "Error",
        description: "Failed to update client information",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      age: client.age || 0,
      gender: client.gender || "other",
      goals: client.goals || "",
      notes: typeof client.notes === 'string' ? client.notes : ""
    });
    setIsEditing(false);
  };

  return {
    isEditing,
    isSaving,
    formData,
    setIsEditing,
    handleChange,
    handleSelectChange,
    handleSave,
    handleCancel
  };
}
